// src/lib/posthog/attribution.ts
//
// Pure traffic-source classification: the nine ordered `traffic_source`
// rules. No `window` access here: the caller resolves the
// current URL, UTM params and `document.referrer` before calling this
// function, so it stays a pure, db-free module that a table-driven test can
// exercise directly without a DOM.

export type TrafficSourceInput = {
  utmSource: string | null;
  utmMedium: string | null;
  gclid: string | null;
  /** The raw referrer URL (e.g. `document.referrer`), or null/empty for none. */
  referrer: string | null;
  /** The current page's own host (e.g. `window.location.hostname`). */
  ownHost: string;
};

// Search-engine and social hosts for rules 7 and 8 below. Entries match the
// exact host or any of its subdomains.
const SEARCH_ENGINE_HOSTS = [
  "google.com",
  "bing.com",
  "duckduckgo.com",
  "yahoo.com",
  "ecosia.org",
  "yandex.com",
  "baidu.com",
];

const SOCIAL_HOSTS = [
  "facebook.com",
  "instagram.com",
  "twitter.com",
  "x.com",
  "linkedin.com",
  "t.co",
  "youtube.com",
  "tiktok.com",
  "reddit.com",
  "telegram.org",
  "telegram.me",
  "t.me",
];

function hostMatches(host: string, entries: string[]): boolean {
  return entries.some(
    (entry) => host === entry || host.endsWith(`.${entry}`),
  );
}

function referrerHostOf(referrer: string | null): string {
  if (!referrer) return "";
  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch {
    return "";
  }
}

/**
 * The origin (scheme + host + port) of a referrer URL, for http(s) URLs
 * only. Only the origin of `document.referrer` leaves
 * the browser, never the full URL — a referring page with a full-URL
 * referrer policy can otherwise leak its own query string (e.g. `order=`).
 * `null` for empty or unparsable input and for a non-http(s) scheme (e.g. an
 * `android-app://` referrer, whose origin is opaque).
 */
export function referrerOrigin(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  return url.origin;
}

/**
 * The nine ordered `traffic_source` rules. Order
 * matters: e.g. a `gclid` (rule 1) wins over a present `utm_source`
 * (rule 4) even though both would otherwise resolve to a value.
 */
export function classifyTrafficSource(input: TrafficSourceInput): string {
  const medium = (input.utmMedium || "").toLowerCase();

  // Rule 1: gclid present or utm_medium in {cpc, ppc} -> paid_search.
  if (input.gclid || medium === "cpc" || medium === "ppc") {
    return "paid_search";
  }
  // Rule 2: utm_medium in {social, paid_social} -> social.
  if (medium === "social" || medium === "paid_social") return "social";
  // Rule 3: utm_medium = email -> email.
  if (medium === "email") return "email";
  // Rule 4: utm_source present -> its value.
  if (input.utmSource) return input.utmSource;

  const referrerHost = referrerHostOf(input.referrer);
  const ownHost = input.ownHost.toLowerCase();

  // Rule 5: no referrer -> direct.
  if (!referrerHost) return "direct";
  // Rule 6: referrer host is our own host -> internal.
  if (referrerHost === ownHost || referrerHost.endsWith(`.${ownHost}`)) {
    return "internal";
  }
  // Rule 7: referrer host is a known search engine -> organic_search.
  if (hostMatches(referrerHost, SEARCH_ENGINE_HOSTS)) return "organic_search";
  // Rule 8: referrer host is a known social host -> social.
  if (hostMatches(referrerHost, SOCIAL_HOSTS)) return "social";
  // Rule 9: anything else -> referral:<host>.
  return `referral:${referrerHost}`;
}
